import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingDown, Users } from "lucide-react";
import { api } from "../../../api";
import { DESK } from "../../../constants/testIds";
import { useDeskData } from "../../../contexts/DeskDataContext";
import { fadeUp } from "../../../lib/motion";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { StatCard } from "../../../components/common/StatCard";
import { GymLoader } from "../../../components/common/GymLoader";

// UX-FD-03 §1 SCR-P02-88: attendance delivered + no-show rate are real, deterministic
// aggregations over Prompt 04's own `booking` records (pt bookings target the provider
// directly; class bookings target a `class_schedule`, resolved here to its `provider_ref` first
// — no separate per-trainer performance read-model exists on the backend, so this is computed
// client-side from real data, never fabricated). "Client retention signal" cross-references the
// trainer's own attended-session clients against Prompt 06's real `/retention/at-risk` list —
// same reuse-before-invent approach, no separate trainer-retention endpoint exists either.
export default function TrainerPerformance() {
  const { branchId } = useDeskData();
  const reducedMotion = useReducedMotion();
  const [providerRef, setProviderRef] = useState("");
  const [loadedRef, setLoadedRef] = useState(null);

  const bookingsQ = useQuery({
    queryKey: ["desk-mgr-perf-bookings", branchId],
    queryFn: async () => (await api.get("/bookings", { params: { branch_id: branchId } })).data.bookings,
    enabled: !!branchId && !!loadedRef,
  });
  const schedulesQ = useQuery({
    queryKey: ["desk-mgr-perf-schedules", branchId],
    queryFn: async () => (await api.get("/class-schedules", { params: { branch_id: branchId } })).data.schedules,
    enabled: !!branchId && !!loadedRef,
  });
  const atRiskQ = useQuery({
    queryKey: ["desk-mgr-perf-at-risk", branchId],
    queryFn: async () => (await api.get("/retention/at-risk")).data.members,
    enabled: !!loadedRef,
  });

  const stats = useMemo(() => {
    if (!loadedRef || !bookingsQ.data || !schedulesQ.data) return null;
    const ownScheduleIds = new Set(schedulesQ.data.filter((s) => s.provider_ref === loadedRef).map((s) => s.id));
    const mine = bookingsQ.data.filter((b) => (b.type === "pt" && b.target_ref === loadedRef) || (b.type === "class" && ownScheduleIds.has(b.target_ref)));
    const attended = mine.filter((b) => b.status === "attended");
    const noShow = mine.filter((b) => b.status === "no_show");
    const total = attended.length + noShow.length;
    const clientRefs = new Set(attended.map((b) => b.member_ref).filter(Boolean));
    const atRiskRefs = new Set((atRiskQ.data || []).map((m) => m.member_ref));
    const atRiskAmongClients = [...clientRefs].filter((ref) => atRiskRefs.has(ref)).length;
    return {
      attended: attended.length, noShow: noShow.length,
      noShowRate: total ? Math.round((noShow.length / total) * 1000) / 10 : 0,
      clientCount: clientRefs.size, atRiskAmongClients,
    };
  }, [loadedRef, bookingsQ.data, schedulesQ.data, atRiskQ.data]);

  return (
    <div data-testid={DESK.mgrPerformancePage} className="space-y-6 max-w-2xl">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>Trainer performance</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>Attendance delivered, no-show rate and a client retention signal — all real, no directory exists to browse trainers, so enter a reference ID directly.</p>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.03)} className="flex gap-2">
        <Input data-testid={DESK.mgrPerformanceProviderInput} placeholder="Trainer/provider reference ID" value={providerRef} onChange={(e) => setProviderRef(e.target.value)} />
        <Button data-testid={DESK.mgrPerformanceLoadButton} disabled={!providerRef.trim()} onClick={() => setLoadedRef(providerRef.trim())} variant="outline" className="shrink-0">Load</Button>
      </motion.div>

      {loadedRef && (bookingsQ.isLoading || schedulesQ.isLoading || atRiskQ.isLoading) && (
        <GymLoader label="Computing performance…" testId="desk-mgr-trainerperf-loader" />
      )}

      {stats && (
        <motion.div {...fadeUp(reducedMotion, 0.06)} className="grid grid-cols-3 gap-3">
          <StatCard testId={DESK.mgrPerformanceAttendedStat} label="Sessions delivered" value={stats.attended} index={0} />
          <StatCard testId={DESK.mgrPerformanceNoShowStat} label="No-show rate" value={stats.noShowRate} formatValue={(v) => `${v}%`} index={1} />
          <StatCard
            testId={DESK.mgrPerformanceRetentionStat}
            label="Clients at-risk"
            value={stats.atRiskAmongClients}
            subtitle={`of ${stats.clientCount} clients trained`}
            index={2}
          />
        </motion.div>
      )}
    </div>
  );
}
